import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, Users, Share2 } from "lucide-react";
import { apiFetch } from "./api/client";
import { UI } from "./designSystem";
import { getEffectiveRoles } from "./roleSimulator";

// Voltar dessas telas de administração deve ir pro painel de admin (/admin),
// não pro site.
const ADMIN_PATH = "/admin";

// Visão só-leitura e reduzida (LGPD) do cadastro de membros, pensada pro
// papel SECRETARIA_CONSULTA -- consome /members/directory (não /members),
// que já devolve só nome, cidade/bairro, status e aniversário sem o ano.
// Sem CPF, endereço completo, telefone/e-mail, batismo ou anotações
// internas -- e sem nenhum botão de editar/excluir. Ver AdminMembros.jsx
// pra tela completa (papel SECRETARIA/ADMIN).
const STATUS_LABELS = {
  ATIVO: "Ativo",
  INATIVO: "Inativo",
  TRANSFERIDO: "Transferido",
  FALECIDO: "Falecido",
  PENDENTE: "Pendente"
};
// Mesmas opcoes/ordem do filtro de status na tela completa (AdminMembros.jsx)
// -- so client-side, o campo "status" ja vem em /members/directory.
const STATUS_OPTIONS = ["ATIVO", "INATIVO", "TRANSFERIDO", "FALECIDO", "PENDENTE"];
const STATUS_COLORS = {
  ATIVO: { border: "rgba(74,222,128,0.45)", background: "rgba(34,197,94,0.14)", color: "#86EFAC" },
  INATIVO: { border: "rgba(148,163,184,0.4)", background: "rgba(148,163,184,0.12)", color: "#c7ced9" },
  TRANSFERIDO: { border: "rgba(125,211,252,0.45)", background: "rgba(14,165,233,0.14)", color: "#7DD3FC" },
  FALECIDO: { border: "rgba(148,163,184,0.4)", background: "rgba(148,163,184,0.08)", color: "#9fb0cc" },
  PENDENTE: { border: "rgba(251,191,36,0.45)", background: "rgba(251,191,36,0.14)", color: "#FDE68A" }
};

const MONTH_NAMES = [
  "jan", "fev", "mar", "abr", "mai", "jun",
  "jul", "ago", "set", "out", "nov", "dez"
];

const MONTH_NAMES_FULL = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

const pageStyle = {
  minHeight: "100vh",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: UI.shell,
  color: UI.text,
  fontFamily: "'DM Sans', system-ui, sans-serif",
  padding: "28px 20px 60px"
};

const backLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  color: "#9fb0cc",
  fontSize: 13,
  fontWeight: 700,
  textDecoration: "none",
  marginBottom: 18
};

const cardStyle = {
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.28)",
  borderRadius: 16,
  padding: 18,
  boxShadow: "0 14px 28px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.06)"
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid rgba(148,163,184,0.32)",
  background: "rgba(255,255,255,0.04)",
  color: "#e5e7eb",
  fontSize: 14,
  fontFamily: "inherit"
};

const selectStyle = { ...inputStyle };

function CenteredMessage({ children }) {
  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 480, margin: "18vh auto 0", textAlign: "center" }}>
        <p style={{ margin: 0, color: "#c7ced9", fontSize: 14, lineHeight: 1.6 }}>{children}</p>
        <Link to={ADMIN_PATH} style={{ ...backLinkStyle, marginTop: 18, justifyContent: "center" }}>
          <ArrowLeft size={14} /> Voltar
        </Link>
      </div>
    </div>
  );
}

export default function AdminMembrosConsulta({ portalAccess } = {}) {
  const [status, setStatus] = useState("checking");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [birthdayMonth, setBirthdayMonth] = useState("");
  const [shareLinkCopied, setShareLinkCopied] = useState(false);

  useEffect(() => {
    // Quando chega via o portal (AdminMembrosPortal.jsx / AdminCriancasPortal.jsx),
    // o papel de quem esta logado ja foi checado uma vez la -- evita bater
    // /auth/me de novo aqui e reaparecer um "Carregando..." por cima do que
    // o portal ja resolveu.
    if (portalAccess !== undefined) {
      setStatus(portalAccess ? "ready" : "forbidden");
      return undefined;
    }
    let alive = true;
    apiFetch("/auth/me", { method: "GET", skipSessionExpiredHandler: true })
      .then((me) => {
        if (!alive) return;
        const effectiveMeRoles = getEffectiveRoles(me);
        const canAccess =
          effectiveMeRoles.includes("ADMIN") ||
          effectiveMeRoles.includes("SECRETARIA") ||
          effectiveMeRoles.includes("SECRETARIA_CONSULTA");
        if (canAccess && !me.passwordMustChange) {
          setStatus("ready");
        } else {
          setStatus("forbidden");
        }
      })
      .catch(() => {
        if (alive) setStatus("forbidden");
      });
    return () => {
      alive = false;
    };
  }, [portalAccess]);

  const loadMembers = useCallback(async (searchTerm) => {
    setLoading(true);
    try {
      const query = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : "";
      const data = await apiFetch(`/members/directory${query}`, { method: "GET" });
      setMembers(Array.isArray(data) ? data : []);
    } catch (error) {
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (status !== "ready") return;
    loadMembers("");
  }, [status, loadMembers]);

  useEffect(() => {
    if (status !== "ready") return;
    const timeout = setTimeout(() => loadMembers(search), 300);
    return () => clearTimeout(timeout);
  }, [search]);

  const statusFilteredMembers = statusFilter
    ? members.filter((member) => (member.status || "ATIVO") === statusFilter)
    : members;

  const visibleMembers = birthdayMonth
    ? statusFilteredMembers.filter((member) => member.birthMonth === Number(birthdayMonth))
    : statusFilteredMembers;

  const copyShareLink = (path) => {
    const url = `${window.location.origin}${path}`;
    navigator.clipboard?.writeText(url).then(() => {
      setShareLinkCopied(true);
      setTimeout(() => setShareLinkCopied(false), 2500);
    });
  };

  if (status === "checking") {
    return <CenteredMessage>Carregando...</CenteredMessage>;
  }

  if (status === "forbidden") {
    return <CenteredMessage>Esta página é restrita à secretaria (inclusive papel de consulta) e administradores.</CenteredMessage>;
  }

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 780, margin: "0 auto" }}>
        <Link to={ADMIN_PATH} style={backLinkStyle}>
          <ArrowLeft size={14} /> Voltar
        </Link>

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
          <div>
            <p style={{ margin: "0 0 6px", fontSize: 11, letterSpacing: 1.35, color: "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>
              Membresia · consulta
            </p>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 950, color: "#FFF" }}>Membros (consulta)</h1>
          </div>
          <button
            onClick={() => copyShareLink("/cadastro/membro")}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 38, padding: "0 14px", border: "1px solid rgba(251,191,36,0.4)", background: "rgba(251,191,36,0.12)", color: "#FDE68A", fontSize: 12, fontWeight: 900, borderRadius: 8, cursor: "pointer" }}
            title="Copia o link do formulário público de cadastro de membro"
          >
            <Share2 size={13} />
            {shareLinkCopied ? "Link copiado!" : "Compartilhar link"}
          </button>
        </div>
        <p style={{ margin: "0 0 20px", color: "#9fb0cc", fontSize: 13, lineHeight: 1.6 }}>
          Visão reduzida do cadastro -- sem CPF, endereço, telefone/e-mail, batismo ou anotações internas. Pra editar ou ver o cadastro completo, use um acesso de secretaria.
        </p>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
          <div style={{ position: "relative", maxWidth: 320, flex: "1 1 220px" }}>
            <Search size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#7f8ba3" }} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por nome ou cidade"
              style={{ ...inputStyle, paddingLeft: 36 }}
            />
          </div>
          <div style={{ maxWidth: 200, flex: "1 1 160px" }}>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} style={selectStyle} title="Filtrar por status">
              <option value="">Todos os status</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>{STATUS_LABELS[option]}</option>
              ))}
            </select>
          </div>
          <div style={{ maxWidth: 220, flex: "1 1 180px" }}>
            <select
              value={birthdayMonth}
              onChange={(event) => setBirthdayMonth(event.target.value)}
              style={selectStyle}
              title="Filtrar aniversariantes do mês"
            >
              <option value="">Aniversariantes -- todos os meses</option>
              {MONTH_NAMES_FULL.map((name, index) => (
                <option key={name} value={String(index + 1)}>Aniversariantes de {name}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={cardStyle}>
          {loading && members.length === 0 && <p style={{ margin: 0, color: "#9fb0cc", fontSize: 13 }}>Carregando...</p>}
          {!loading && visibleMembers.length === 0 && (
            <p style={{ margin: 0, color: "#9fb0cc", fontSize: 13 }}>
              {birthdayMonth
                ? `Nenhum membro faz aniversário em ${MONTH_NAMES_FULL[Number(birthdayMonth) - 1]}.`
                : search.trim()
                ? `Nenhum membro encontrado para "${search}".`
                : statusFilter
                ? `Nenhum membro com o status "${STATUS_LABELS[statusFilter] || statusFilter}" agora.`
                : "Nenhum cadastro encontrado."}
            </p>
          )}
          {visibleMembers.map((member, index) => {
            const statusStyle = STATUS_COLORS[member.status] || STATUS_COLORS.ATIVO;
            return (
              <div
                key={member.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto",
                  gap: 14,
                  alignItems: "center",
                  padding: "14px 0",
                  borderTop: index === 0 ? "none" : "1px solid rgba(255,255,255,0.08)"
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: 0, color: "#E5E7EB", fontSize: 14, fontWeight: 850 }}>
                    {member.fullName}
                    {member.birthMonth && member.birthDay && (
                      <span style={{ color: "#9fb0cc", fontWeight: 700 }}> · aniversário {String(member.birthDay).padStart(2, "0")}/{MONTH_NAMES[member.birthMonth - 1]}</span>
                    )}
                  </p>
                  <p style={{ margin: "3px 0 0", color: "#9fb0cc", fontSize: 12 }}>
                    {[member.city, member.neighborhood].filter(Boolean).join(" · ") || "Sem cidade/bairro cadastrado"}
                    {member.householdId && (
                      <span style={{ color: "#f0a84e" }}>
                        {" "}· <Users size={11} style={{ verticalAlign: "-2px" }} /> vinculado(a) a uma família
                      </span>
                    )}
                  </p>
                </div>
                <span
                  style={{
                    padding: "6px 12px",
                    borderRadius: 999,
                    border: `1px solid ${statusStyle.border}`,
                    background: statusStyle.background,
                    color: statusStyle.color,
                    fontSize: 11,
                    fontWeight: 900,
                    whiteSpace: "nowrap"
                  }}
                >
                  {STATUS_LABELS[member.status] || member.status}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
